import { MouseEvent } from "react";
import Image from "next/image";
import { InlineEditableInput } from "@/components/ui/InlineEditableInput";

import { Folder } from "../types";
import { buildAssetUrl } from "../utils";

type FolderGridProps = {
  folders: Folder[];
  inlineFolderName: string | null;
  onInlineNameChange: (value: string) => void;
  onInlineCreate: (name: string) => void;
  onInlineCancel: () => void;
  selectedFolderIds: string[];
  onFolderClick: (id: string) => void;
  onFolderContextMenu: (id: string, event: MouseEvent) => void;
  onToggleFolderSelection: (id: string, checked: boolean) => void;
  onRenameFolder: (id: string, name: string) => void;
  renameFolderId: string | null;
  showCheckbox?: boolean;
  enableContextMenu?: boolean;
};

export function FolderGrid({
  folders,
  inlineFolderName,
  onInlineNameChange,
  onInlineCreate,
  onInlineCancel,
  selectedFolderIds,
  onFolderClick,
  onFolderContextMenu,
  onToggleFolderSelection,
  onRenameFolder,
  renameFolderId,
  showCheckbox = true,
  enableContextMenu = true,
}: FolderGridProps) {
  return (
    <>
      {inlineFolderName !== null ? (
        <div className="bg-pinkCustom/10 flex cursor-pointer flex-col gap-2 rounded border border-gray-300 p-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              className="accent-pinkCustom h-4 w-4"
              disabled
            />
            <span>📁</span>
          </div>
          <input
            placeholder="New Folder"
            autoFocus
            className="w-full rounded border px-2 py-1 text-xs focus:border-gray-300 focus:bg-white focus:outline-none"
            value={inlineFolderName}
            onChange={(e) => onInlineNameChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onInlineCreate(inlineFolderName);
              }
              if (e.key === "Escape") onInlineCancel();
            }}
            onBlur={() => onInlineCreate(inlineFolderName)}
          />
          <div className="text-[11px] text-gray-500">Folder</div>
        </div>
      ) : null}
      {folders.map((folder) => {
        const isSelected = selectedFolderIds.includes(folder.id);
        const isRenaming = renameFolderId === folder.id;
        return (
          <div
            key={folder.id}
            className={`flex cursor-pointer flex-col gap-2 rounded border border-gray-300 p-3 ${
              isSelected ? "bg-pinkCustom/15" : "hover:bg-pinkCustom/5"
            }`}
            onClick={() => onFolderClick(folder.id)}
            onContextMenu={(e) => {
              if (!enableContextMenu) return;
              e.preventDefault();
              onFolderContextMenu(folder.id, e);
            }}
          >
            <div className="flex items-center gap-2">
              {showCheckbox ? (
                <input
                  type="checkbox"
                  className="accent-pinkCustom h-4 w-4"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleFolderSelection(folder.id, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : null}
              {folder.previewAsset ? (
                <Image
                  src={buildAssetUrl(folder.previewAsset)}
                  alt={`${folder.name} product hero`}
                  width={48}
                  height={48}
                  className="h-12 w-12 rounded object-cover"
                  unoptimized
                />
              ) : (
                <span>📁</span>
              )}
            </div>
            <div
              className="text-sm font-semibold"
              onClick={(e) => e.stopPropagation()}
            >
              <InlineEditableInput
                value={folder.name}
                onSave={(next) => onRenameFolder(folder.id, next)}
                className="truncate"
                startEditing={isRenaming}
                enableClickToEdit={false}
              />
            </div>
            <div className="text-[11px] text-gray-500">Folder</div>
          </div>
        );
      })}
    </>
  );
}
